/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2025-02-10 15:21:56                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-03-03 14:58:36                               *
 *****************************************************************************/

/* SUMMARY
  * Imports
  * Components
  * WorkorderListItem: Display workorder list item
*/

/* Imports */
import { Typography } from "@material-tailwind/react";
import { ShieldCheckIcon, ShieldExclamationIcon } from "@heroicons/react/24/outline";
import { t } from "i18next";
/***/

/* Components */
import CustomerDisplay from "../customer/CustomerDisplay";
import ContactDisplay from "../contact/ContactDisplay";
import TechnicianDisplay from "../technician/TechnicianDisplay";
import WorkordersStatusDisplay from "../workorder-status/WorkordersStatusDisplay";
import SuspensionReasonBadge from "../workorder-status/SuspensionReasonBadge";
import AddressDisplay from "../address/AddressDisplay";
import WorkorderTypeDisplay, { getTranslatedWorkorderTypeLabel } from "../workorder-type/WorkorderTypeDisplay";
import WorkorderOriginDisplay, { getTranslatedWorkorderOriginLabel } from "../workorder-origin/WorkorderOriginDisplay";
import WorkorderAdminActions from "../../../views/super-admin/views/interventions/WorkorderAdminActions";
import { WorkorderStatus } from "../../../types/workorder/workorderStatus";
import { OverflowTooltip } from "../../table/TableCellTooltip";
/***/

/**
* WorkorderListItem: Display workorder list item
* @param workorder - Workorder data
* @param onClick - On click event
* @param isLast - Is last item
*/
export default function WorkorderListItem({
  workorder,
  onClick,
  isLast = false,
  onChange,
  isAdminComponent,
  customer,
  showTypeColumn = true,
  showOriginColumn = true,
  isSmallScreen = false
}: {
  workorder: EasyTech.Workorder,
  onClick?: () => void,
  isLast?: boolean,
  onChange?: () => void,
  isAdminComponent?: boolean,
  customer?: EasyTech.Customer,
  showTypeColumn?: boolean,
  showOriginColumn?: boolean,
  isSmallScreen?: boolean
}) {
  const href = `/workorders/${workorder.id}`;
  const firstContact = workorder.contacts?.[0]?.contact;
  const contactLabel = firstContact ? `${firstContact.firstName} ${firstContact.lastName}`.trim() : "-";
  const technicianLabel = workorder.technician
    ? `${workorder.technician.firstName} ${workorder.technician.lastName}`.trim()
    : "-";
  const workorderTypeLabel = getTranslatedWorkorderTypeLabel(workorder.type);
  const workorderOriginLabel = getTranslatedWorkorderOriginLabel(workorder.origin);

  // Gestion des raccourcis clavier et menu contextuel
  const handleRowClick = (e: React.MouseEvent<HTMLTableRowElement>) => {
    // CTRL/CMD + click pour ouvrir dans un nouvel onglet
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      window.open(href, "_blank");
      return;
    }

    // Clic normal
    onClick?.();
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLTableRowElement>) => {
    // Bouton du milieu (scroll) pour ouvrir dans un nouvel onglet
    if (e.button === 1) {
      e.preventDefault();
      window.open(href, "_blank");
    }
  };

  return (
    <tr
      className={`page-list-item${isLast ? " last" : ""}`}
      tabIndex={0}
      style={{ cursor: "pointer" }}
      onClick={handleRowClick}
      onMouseDown={handleMouseDown}
      data-href={href}
    >
      <td>
        <a href={href} className="block w-full h-full" onClick={(e) => e.preventDefault()}>
          <OverflowTooltip content={workorder.ref} className="et-cell-truncate">
            <Typography variant="small" color="blue-gray">
              {workorder.ref}
            </Typography>
          </OverflowTooltip>
        </a>
      </td>

      {customer === undefined &&
        <td>
          <a href={href} className="block w-full h-full" onClick={(e) => e.preventDefault()}>
            <div className="et-cell-wrap">
              <CustomerDisplay customer={workorder.customer} size="small" noToolTip />
            </div>
          </a>
        </td>
      }

      <td>
        <a href={href} className="block w-full h-full" onClick={(e) => e.preventDefault()}>
          <OverflowTooltip content={contactLabel} className="et-cell-truncate">
            <ContactDisplay contact={firstContact} noToolTip />
          </OverflowTooltip>
        </a>
      </td>

      <td>
        <a href={href} className="block w-full h-full" onClick={(e) => e.preventDefault()}>
          <OverflowTooltip content={technicianLabel} className="et-cell-truncate">
            {workorder.technician && workorder.technician.firstName && workorder.technician.lastName ?
              <TechnicianDisplay technician={workorder.technician} size="small" noToolTip />
              : "-"}
          </OverflowTooltip>
        </a>
      </td>

      <td>
        <a href={href} className="block w-full h-full" onClick={(e) => e.preventDefault()}>
          <div className={isSmallScreen ? "et-cell-centered" : "flex flex-col gap-1 items-start"}>
            <WorkordersStatusDisplay status={workorder.status as WorkorderStatus} iconOnly={isSmallScreen} />
            {/*
              * The reason a technician gave for pausing, read straight off the board: this is what
              * spares the planner a call to ask why an intervention is stuck. Not shown when the
              * screen is small enough that iconOnly is already trimming this column, and no note --
              * a free-text note belongs on the detail page, not stretching a list row.
              */}
            {!isSmallScreen && (
              <SuspensionReasonBadge reason={workorder.suspensionReason} note={null} />
            )}
          </div>
        </a>
      </td>

      <td>
        <a href={href} className="block w-full h-full" onClick={(e) => e.preventDefault()}>
          <div className="et-cell-wrap">
            <AddressDisplay address={workorder.address} zone={workorder.zone} size="small" noToolTip />
          </div>
        </a>
      </td>

      {showTypeColumn && (
        <td>
          <OverflowTooltip content={workorderTypeLabel} className="et-cell-truncate">
            <WorkorderTypeDisplay type={workorder.type} size="sm" />
          </OverflowTooltip>
        </td>
      )}

      {showOriginColumn && (
        <td>
          <OverflowTooltip content={workorderOriginLabel} className="et-cell-truncate">
            <WorkorderOriginDisplay origin={workorder.origin} size="sm" />
          </OverflowTooltip>
        </td>
      )}

      <td>
        <a href={href} className="block w-full h-full" onClick={(e) => e.preventDefault()}>
          <div className="flex justify-center items-center">
            {workorder.safetyDiagStatus === "VALID" ? (
              <ShieldCheckIcon
                className="w-5 shrink-0 text-green-500"
                title={t("workorder:filters:safetyDiag:valid", "Valide")}
              />
            ) : workorder.safetyDiagStatus === "INVALID" ? (
              <ShieldExclamationIcon
                className="w-5 shrink-0 text-red-500"
                title={t("workorder:filters:safetyDiag:invalid", "Invalide")}
              />
            ) : (
              <Typography variant="small" color="blue-gray">—</Typography>
            )}
          </div>
        </a>
      </td>

      {isAdminComponent === true &&
        <td className="z-10" data-no-link onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}>
          <WorkorderAdminActions
            workorder={workorder}
            onChange={() => { if (onChange) onChange(); }}
          />
        </td>
      }
    </tr>
  );
}
